import { Field, RequiredMark, Select } from "./ui";
import { AddressSearch } from "./AddressSearch";
import { PROVINCES } from "@/lib/format";
import { isAddressFieldRequired } from "@/lib/required";
import type { Address } from "@/lib/types";

export const EMPTY_ADDRESS: Address = {
  contact_name: "",
  phone: "",
  email: "",
  company_name: "",
  street: "",
  complex: "",
  suburb: "",
  city: "",
  province: "Gauteng",
  postal_code: "",
  instructions: "",
};

export function AddressForm({
  value,
  onChange,
  errors = {},
  prefix,
  authenticated = true,
}: {
  value: Address;
  onChange: (next: Address) => void;
  errors?: Record<string, string>;
  prefix: "collection" | "delivery";
  /** False where there is no session yet, such as guest checkout. */
  authenticated?: boolean;
}) {
  // Editing any of these by hand invalidates a coordinate that came from a
  // suggestion: the person is describing a different place now, and pricing a
  // route from the old pin would be worse than geocoding the new text.
  const LOCATION_FIELDS = new Set<keyof Address>(["street", "suburb", "city", "postal_code", "province"]);

  const set = (key: keyof Address) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const next: Address = { ...value, [key]: event.target.value };
    if (LOCATION_FIELDS.has(key)) {
      next.latitude = undefined;
      next.longitude = undefined;
    }
    onChange(next);
  };

  const error = (key: string) => errors[`${prefix}.${key}`];
  // Stars come from the same list that blocks the button, so the two can never
  // tell the customer different things. Everything here is needed to book;
  // a quote needs only the location half, and the step that quotes says so.
  const req = (field: keyof Address) => isAddressFieldRequired(field);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <p className="text-xs text-ink-400 sm:col-span-2">
        <RequiredMark /> is a field we need before this can go.
      </p>
      <Field label="Contact name" name={`${prefix}-contact`} required={req("contact_name")} value={value.contact_name} onChange={set("contact_name")} error={error("contact_name")} />
      <Field label="Phone" name={`${prefix}-phone`} inputMode="tel" required={req("phone")} value={value.phone} onChange={set("phone")} error={error("phone")} />
      <Field label="Company (optional)" name={`${prefix}-company`} value={value.company_name ?? ""} onChange={set("company_name")} className="sm:col-span-2" />

      {/* One field, not two. The street box is the address lookup: typing in it
          searches, picking a suggestion fills the suburb, city, province and
          postal code underneath. */}
      <AddressSearch
        value={value}
        onChange={onChange}
        authenticated={authenticated}
        name={`${prefix}-street`}
        required={req("street")}
        error={error("street")}
        className="sm:col-span-2"
      />
      <Field label="Complex or unit (optional)" name={`${prefix}-complex`} value={value.complex ?? ""} onChange={set("complex")} />
      <Field label="Suburb" name={`${prefix}-suburb`} required={req("suburb")} value={value.suburb} onChange={set("suburb")} error={error("suburb")} />
      <Field label="City" name={`${prefix}-city`} required={req("city")} value={value.city} onChange={set("city")} error={error("city")} />
      <Select
        label="Province"
        name={`${prefix}-province`}
        required={req("province")}
        value={value.province}
        onChange={set("province")}
        error={error("province")}
        options={PROVINCES.map((p) => ({ value: p, label: p }))}
      />
      <Field
        label="Postal code"
        name={`${prefix}-postal`}
        required={req("postal_code")}
        inputMode="numeric"
        maxLength={4}
        value={value.postal_code}
        onChange={set("postal_code")}
        error={error("postal_code")}
      />
      <Field
        label="Instructions for the driver (optional)"
        name={`${prefix}-instructions`}
        value={value.instructions ?? ""}
        onChange={set("instructions")}
        className="sm:col-span-2"
        hint="Gate codes, buzzer numbers, where to find reception."
      />
    </div>
  );
}
